import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock } from 'lucide-react';
import { productService } from '@/services/product.service';
import { Product } from '@/types';
import { cn } from '@/utils';

interface SearchBarProps {
  className?: string;
  mobile?: boolean;
  onClose?: () => void;
}

export const SearchBar = ({ className, mobile, onClose }: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ products: Product[]; categories: { id: string; name: string; slug: string }[] }>({ products: [], categories: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<string[]>(() => JSON.parse(localStorage.getItem('mm_search') || '[]'));
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 2) { setSuggestions({ products: [], categories: [] }); return; }
    setLoading(true);
    try {
      const res = await productService.search(q, 1, 5);
      setSuggestions({ products: res.data.data.products.slice(0, 5), categories: res.data.data.categories.slice(0, 3) });
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchSuggestions]);

  const handleSearch = (q: string) => {
    if (!q.trim()) return;
    const h = [q, ...history.filter(i => i !== q)].slice(0, 8);
    setHistory(h);
    localStorage.setItem('mm_search', JSON.stringify(h));
    setOpen(false);
    setQuery('');
    navigate(`/search?q=${encodeURIComponent(q)}`);
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch(query);
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={cn('relative', className)}>
      <div className={cn(
        'flex items-center border-2 rounded-xl transition-all bg-white',
        open ? 'border-primary-400 shadow-md' : 'border-gray-200 hover:border-primary-300'
      )}>
        <Search className="h-4 w-4 text-gray-400 ml-3 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search medicines, vitamins, skincare..."
          className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none text-gray-800 placeholder:text-gray-400"
        />
        {query && (
          <button onClick={() => { setQuery(''); setSuggestions({ products: [], categories: [] }); }} className="mr-2 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        )}
        <button onClick={() => handleSearch(query)} className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2.5 rounded-r-xl text-sm font-medium transition-colors shrink-0">
          Search
        </button>
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-40 overflow-hidden max-h-[70vh] overflow-y-auto">
            {suggestions.categories.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-50">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Categories</p>
                {suggestions.categories.map(c => (
                  <button key={c.id} onClick={() => { navigate(`/categories/${c.slug}`); setOpen(false); onClose?.(); }} className="block w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-primary-50 rounded-lg hover:text-primary-700">
                    🏷️ {c.name}
                  </button>
                ))}
              </div>
            )}

            {suggestions.products.length > 0 && (
              <div className="px-4 py-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Products</p>
                {suggestions.products.map(p => (
                  <button key={p.id} onClick={() => { navigate(`/products/${p.slug}`); setOpen(false); onClose?.(); }} className="flex items-center gap-3 w-full px-2 py-2 hover:bg-primary-50 rounded-lg transition-colors">
                    <img src={p.images[0] || '/placeholder-product.jpg'} alt={p.name} className="w-10 h-10 object-cover rounded-lg" />
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">{p.name}</p>
                      <p className="text-xs text-primary-600 font-semibold">₹{(p.discountedPrice || p.price).toFixed(2)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {query.length >= 2 && suggestions.products.length === 0 && !loading && (
              <p className="px-4 py-4 text-sm text-gray-500 text-center">No results for "{query}"</p>
            )}

            {!query && history.length > 0 && (
              <div className="px-4 py-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recent Searches</p>
                {history.map((h, i) => (
                  <button key={i} onClick={() => { setQuery(h); handleSearch(h); }} className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">
                    <Clock className="h-3 w-3 text-gray-400" /> {h}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
